import dayjs, { type Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type { SurveyDateInputFormat, SurveyQuestion } from './survey-question.types'

dayjs.extend(customParseFormat)

/** Campaign draft storage — ISO calendar date (yyyy-mm-dd). */
export const SURVEY_DATE_STORAGE_FMT = 'YYYY-MM-DD'

/** Figma 5074-13335 — in-field placeholder when definition omits one. */
export const SURVEY_DATE_DEFAULT_PLACEHOLDER = 'Select date...'

export function surveyDatePickerFormatToken(display: SurveyDateInputFormat): string {
  return display === 'MM/dd/yyyy' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
}

/** Optional caption above the field (reserved — builder placeholder goes in-field). */
export function dateHintLabel(question: SurveyQuestion): string | null {
  if (question.type !== 'date') return null
  const hint = question.description?.trim()
  return hint && hint.length > 0 ? hint : null
}

/** In-field placeholder (aligned with short/long text questions). */
export function dateFieldPlaceholder(question: SurveyQuestion): string {
  const hint = question.placeholder?.trim()
  return hint && hint.length > 0 ? hint : SURVEY_DATE_DEFAULT_PLACEHOLDER
}

export function dayFromSurveyStorageIso(iso: string): Dayjs | null {
  const t = iso.trim()
  if (!t) return null
  const d = dayjs(t, SURVEY_DATE_STORAGE_FMT, true)
  return d.isValid() ? d.startOf('day') : null
}

export function surveyStorageIsoFromDay(value: Dayjs | null): string {
  if (!value?.isValid()) return ''
  return value.startOf('day').format(SURVEY_DATE_STORAGE_FMT)
}

function parseOptionalBound(raw: string | null): Dayjs | undefined {
  if (!raw?.trim()) return undefined
  const d = dayFromSurveyStorageIso(raw)
  return d?.isValid() ? d : undefined
}

function pinchMinWithPastRestriction(minDate: Dayjs | undefined, today: Dayjs): Dayjs {
  if (minDate == null) return today
  return minDate.isAfter(today) ? minDate : today
}

function pinchMaxWithFutureRestriction(maxDate: Dayjs | undefined, today: Dayjs): Dayjs {
  if (maxDate == null) return today
  return maxDate.isBefore(today) ? maxDate : today
}

export function surveyDateSelectableBounds(question: SurveyQuestion): {
  minDate: Dayjs | undefined
  maxDate: Dayjs | undefined
} {
  const today = dayjs().startOf('day')
  let minDate = parseOptionalBound(question.earliestSelectableDate)
  let maxDate = parseOptionalBound(question.latestSelectableDate)

  if (question.disablePastDates) {
    minDate = pinchMinWithPastRestriction(minDate, today)
  }
  if (question.disableFutureDates) {
    maxDate = pinchMaxWithFutureRestriction(maxDate, today)
  }

  if (minDate && maxDate && minDate.isAfter(maxDate)) {
    return { minDate: undefined, maxDate: undefined }
  }

  return { minDate, maxDate }
}

export function isDateAnswerEmpty(value: string | null): boolean {
  return value == null || value.trim() === ''
}
